/**
 * The mixing desk (07 §2): one `AudioContext` and four buses into a limited master.
 *
 *   world -> muffle (arctic low-pass, -3 dB) -> [sfx slider] -+-> master -> limiter -> mono stage -> out
 *                                         '-> reverb send ----'
 *   ui ------------------------------------> [sfx slider] ----'
 *   music ---------------------------------> [music slider] --+ (plus a little of the same reverb)
 *   ambience ------------------------------> [ambience slider]'
 */

import { type AudioSettings, sliderGain } from '../settings';
import { ARCTIC_MUFFLE, OPEN_AIR } from '../spatial';
import { renderImpulse, renderNoise } from '../synth/impulse';

const RAMP = 0.05;
const WORLD_REVERB = 0.2;
const MUSIC_REVERB = 0.14;

/** Whatever this browser calls it; null where there is no WebAudio at all (node, a locked-down webview). */
export function audioContextClass(): typeof AudioContext | null {
  const scope = globalThis as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext };
  return scope.AudioContext ?? scope.webkitAudioContext ?? null;
}

export class Graph {
  readonly ctx: AudioContext;
  readonly world: GainNode;
  readonly ui: GainNode;
  readonly music: GainNode;
  readonly ambience: GainNode;
  /** Two seconds of white noise, shared by everything that is made of air. */
  readonly noise: AudioBuffer;

  private readonly muffleFilter: BiquadFilterNode;
  private readonly muffleGain: GainNode;
  private readonly worldSlider: GainNode;
  private readonly uiSlider: GainNode;
  private readonly musicSlider: GainNode;
  private readonly ambienceSlider: GainNode;
  private readonly master: GainNode;
  private readonly monoStage: GainNode;

  constructor(Ctor: typeof AudioContext) {
    const ctx = new Ctor({ latencyHint: 'interactive' });
    this.ctx = ctx;
    this.world = ctx.createGain();
    this.ui = ctx.createGain();
    this.music = ctx.createGain();
    this.ambience = ctx.createGain();
    this.muffleFilter = ctx.createBiquadFilter();
    this.muffleFilter.type = 'lowpass';
    this.muffleFilter.frequency.value = OPEN_AIR.cutoff;
    this.muffleFilter.Q.value = 0.5;
    this.muffleGain = ctx.createGain();
    this.worldSlider = ctx.createGain();
    this.uiSlider = ctx.createGain();
    this.musicSlider = ctx.createGain();
    this.ambienceSlider = ctx.createGain();
    this.master = ctx.createGain();

    const reverb = ctx.createConvolver();
    const [left, right] = renderImpulse(ctx.sampleRate, 1.6, 1987);
    const impulse = ctx.createBuffer(2, left.length, ctx.sampleRate);
    impulse.copyToChannel(left, 0);
    impulse.copyToChannel(right, 1);
    reverb.buffer = impulse;
    const worldSend = ctx.createGain();
    worldSend.gain.value = WORLD_REVERB;
    const musicSend = ctx.createGain();
    musicSend.gain.value = MUSIC_REVERB;

    // The limiter: fast, hard, and only there for the moment three blasts land on one frame.
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -6;
    limiter.knee.value = 3;
    limiter.ratio.value = 20;
    limiter.attack.value = 0.002;
    limiter.release.value = 0.2;
    // Mono is a channel-count change on the last node: the graph folds stereo down for us.
    this.monoStage = ctx.createGain();
    this.monoStage.channelCountMode = 'explicit';
    this.monoStage.channelInterpretation = 'speakers';
    this.monoStage.channelCount = 2;

    this.world.connect(this.muffleFilter).connect(this.muffleGain).connect(this.worldSlider).connect(this.master);
    this.worldSlider.connect(worldSend).connect(reverb);
    this.ui.connect(this.uiSlider).connect(this.master);
    this.music.connect(this.musicSlider).connect(this.master);
    this.musicSlider.connect(musicSend).connect(reverb);
    reverb.connect(this.master);
    this.ambience.connect(this.ambienceSlider).connect(this.master);
    this.master.connect(limiter).connect(this.monoStage).connect(ctx.destination);

    const white = renderNoise(ctx.sampleRate, 2, 0x1987);
    this.noise = ctx.createBuffer(1, white.length, ctx.sampleRate);
    this.noise.copyToChannel(white, 0);
  }

  get now(): number {
    return this.ctx.currentTime;
  }

  /** Browsers start a context suspended until a user gesture; calling this again later is harmless. */
  resume(): void {
    if (this.ctx.state === 'suspended') void this.ctx.resume().catch(() => undefined);
  }

  apply(s: AudioSettings): void {
    const t = this.now;
    this.master.gain.setTargetAtTime(sliderGain(s.master), t, RAMP);
    this.worldSlider.gain.setTargetAtTime(sliderGain(s.sfx), t, RAMP);
    this.uiSlider.gain.setTargetAtTime(sliderGain(s.sfx), t, RAMP);
    this.musicSlider.gain.setTargetAtTime(sliderGain(s.music), t, RAMP);
    this.ambienceSlider.gain.setTargetAtTime(sliderGain(s.ambience), t, RAMP);
    this.monoStage.channelCount = s.mono ? 1 : 2;
  }

  /** Arctic missions muffle every sound in the world (01 §10); menus and music are untouched. */
  setMuffle(on: boolean): void {
    const m = on ? ARCTIC_MUFFLE : OPEN_AIR;
    const t = this.now;
    this.muffleFilter.frequency.setTargetAtTime(m.cutoff, t, RAMP);
    this.muffleGain.gain.setTargetAtTime(m.gain, t, RAMP);
  }

  /** A looping source of the shared noise buffer, started. The caller connects and owns it. */
  noiseSource(): AudioBufferSourceNode {
    const src = this.ctx.createBufferSource();
    src.buffer = this.noise;
    src.loop = true;
    // Start each reader somewhere else in the buffer so two of them never play in unison.
    src.start(0, Math.random() * 1.5);
    return src;
  }

  dispose(): void {
    void this.ctx.close().catch(() => undefined);
  }
}
